/**
 * <ActionSheet> — a bottom-anchored menu of actions (molecule, L0.1).
 *
 * The maison version of the legacy `MediaPickerButton`'s `renderMenuOptions`: a sheet that slides up
 * from the bottom on a phone (a centered card on a large screen, like `<Modal>`) holding a short list of tappable rows (icon + label). Generic — it takes `items`
 * ({ icon, label, onPress }); no domain coupling — so it serves the media picker or any action menu.
 *
 * The portal/scrim/backdrop/back+Escape plumbing lives in the shared `<Overlay>` atom (a JS-layer paper
 * `<Portal>` — never RN's native `<Modal>`, whose iOS `UIViewController` would block an in-sheet image
 * picker / PHPicker; that's also why we don't reuse `<ThreeDotMenu>`, a native-`Modal` anchored popover).
 *
 * Each row reuses the `<LabeledIconButton>` atom (ghost, full-width) — never a hand-rolled icon+label.
 * Opening the sheet retracts the keyboard so it doesn't fight the sheet for the bottom of the screen.
 *
 * `footer` (optionnel) : ce qui se pose sous les lignes, dans la feuille — un réglage plutôt qu'une action
 * (« Apparence » dans « Plus » du live du masteur). Absent : rendu inchangé.
 */
import React, { useContext, useEffect } from "react"
import { Dimensions, Keyboard, View } from "react-native"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import { StyleSheet } from "react-native-unistyles"

import { ScrollView } from "../../atoms/ScrollView"
import { DimensionsContext } from "../../../common/utils/mobileUtils"
import { Overlay, SHEET_EDGE_WIDTH } from "../../atoms/Overlay"
import { LabeledIconButton } from "../../atoms/Buttons/LabeledIconButton"
import { Divider } from "../../atoms/Divider"
import { Text } from "../../atoms/typography/Text"
import { type IconName } from "../../atoms/icons/Icon"
import { shadowStyle } from "../../utils/shadow"

/** One sheet row: a leading glyph, a label, and its action. */
type ActionSheetItem = {
    icon: IconName
    label: string
    onPress: () => void
    disabled?: boolean
}

export type ActionSheetProps = {
    visible: boolean
    onClose: () => void
    items: ActionSheetItem[]
    /** Optional muted heading above the rows. */
    title?: string
    /**
     * Défaut vrai : toucher une ligne referme la feuille (`onClose`) avant son action. Faux quand la
     * ligne fait avancer un parcours qui tient la visibilité de la feuille lui-même (choisir un jour
     * ouvre la confirmation) : un `onClose` y fermerait le parcours entier, étape suivante comprise.
     */
    closeOnSelect?: boolean
    /** Sous les lignes, dans la feuille : un réglage qui n'est pas une action. Absent : rien. */
    footer?: React.ReactNode
    testID?: string
}

export function ActionSheet(props: ActionSheetProps) {
    const { visible, onClose, items, title, closeOnSelect = true, footer, testID } = props

    const dimensions = useContext(DimensionsContext) ?? Dimensions.get("window")
    const insets = useSafeAreaInsets()
    // Même seuil et même largeur que `<Modal>`. Étirée sur 1 500 px, une feuille de choix pose ses
    // libellés à un mètre l'un de l'autre et cesse de se lire comme une liste. Et comme `<Modal>`, au
    // grand écran elle se pose au milieu : ouverte depuis une boîte centrée (« Signaler un problème »
    // puis « Joindre »), elle passe par-dessus la boîte au lieu de rester collée au bas de l'écran.
    const isLarge = dimensions.width > 800

    useEffect(() => {
        // A fresh choice surface — retract the keyboard rather than let it overlap the sheet.
        if (visible) Keyboard.dismiss()
    }, [visible])

    return (
        <Overlay visible={visible} onRequestClose={onClose} placement={isLarge ? "center" : "bottom"} testID={testID}>
            <View style={styles.sheet(insets.bottom, isLarge)}>
                {!isLarge && <View style={styles.handle} />}
                {title != null && (
                    <Text size="text-sm" color="text-muted" style={styles.title}>
                        {title}
                    </Text>
                )}
                <ScrollView
                    style={styles.scroll(dimensions.height)}
                    keyboardShouldPersistTaps="handled"
                    showsVerticalScrollIndicator={false}
                >
                    {items.map((item, index) => (
                        <React.Fragment key={`${item.label}-${index}`}>
                            <LabeledIconButton
                                variant="ghost"
                                size="md"
                                fullWidth
                                iconName={item.icon}
                                label={item.label}
                                disabled={item.disabled}
                                onPress={() => {
                                    if (closeOnSelect) onClose()
                                    item.onPress()
                                }}
                            />
                            {index < items.length - 1 && <Divider spacing="0" />}
                        </React.Fragment>
                    ))}
                    {footer}
                </ScrollView>
            </View>
        </Overlay>
    )
}

const styles = StyleSheet.create((theme) => ({
    sheet: (bottomInset: number, isLarge: boolean) => ({
        width: isLarge ? 500 : ("100%" as const),
        backgroundColor: theme.colors["surface-raised"],
        borderTopLeftRadius: theme.radius.lg,
        borderTopRightRadius: theme.radius.lg,
        // Au milieu du grand écran, une carte : ses quatre coins arrondis, sans poignée ni liseré.
        borderBottomLeftRadius: isLarge ? theme.radius.lg : 0,
        borderBottomRightRadius: isLarge ? theme.radius.lg : 0,
        // Le liseré du bord haut d'une feuille qui monte : visible sur la nuit, élève et parent (`sheet-edge`).
        borderTopWidth: isLarge ? 0 : SHEET_EDGE_WIDTH,
        borderColor: theme.colors["sheet-edge"],
        paddingTop: theme.spacing["2"],
        paddingHorizontal: theme.spacing["2"],
        // Clear the home indicator even when the list doesn't scroll (the bottom sheet only).
        paddingBottom: isLarge ? theme.spacing["2"] : Math.max(bottomInset, theme.spacing["5"]),
        ...shadowStyle(theme, "sheet"),
    }),
    // maxHeight on the scroll area (not the sheet) so it reliably scrolls past half the screen.
    scroll: (height: number) => ({
        maxHeight: height / 2,
    }),
    handle: {
        width: 40,
        height: 4,
        borderRadius: theme.radius.full,
        backgroundColor: theme.colors.border,
        alignSelf: "center" as const,
        marginBottom: theme.spacing["3"],
    },
    title: {
        paddingHorizontal: theme.spacing["4"],
        paddingBottom: theme.spacing["1"],
    },
}))
